/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

import isArray from 'lodash/isArray';
import isString from 'lodash/isString';

/**
 * Továrna na cesty v souborovém systému.
 */
class FileSystemPathFactory {
  /**
   * Vytvoří instanci.
   * @param {Class.<FileSystemPath>} FileSystemPath Konstruktor cesty.
   * @param {PathBrowserify} pathBrowserify
   */
  constructor(FileSystemPath, pathBrowserify) {
    this.FileSystemPath = FileSystemPath;
    this.pathBrowserify = pathBrowserify;
  }

  /**
   * Vytvoří cestu z informací o jejích částech.
   * @param {Object} [opts={}] Nepovinné parametry.
   * @param {FileSystemName} [opts.base=''] Název záznamu.
   * @param {FileSystemName} [opts.extension=''] Přípona názvu záznamu.
   * @param {FileSystemName} [opts.unit=''] Název jednotky.
   * @param {Array.<FileSystemName>} [opts.parents=[]] Seznam názvů rodičovských složek.
   * @param {FileSystemName} [opts.stem=''] Část názvu záznamu bez přípony.
   * @returns {FileSystemPath}
   */
  fromInfo({
    base = '',
    extension = '',
    unit = '',
    parents = [],
    stem = ''
  } = {}) {
    const segmentArray = [];
    if (!isString(unit)) {
      throw new TypeError(`Invalid unit "${unit}".`);
    }
    if (unit) {
      segmentArray.push(`/${unit}`);
    }
    if (!isArray(parents)) {
      throw new TypeError(`Invalid parents "${parents}".`);
    }
    parents.forEach((parent) => {
      if (!isString(parent)) {
        throw new TypeError(`Invalid parent "${parent}".`);
      }
      segmentArray.push(parent);
    });
    if (!isString(base)) {
      throw new TypeError(`Invalid base "${base}".`);
    }
    if (base) {
      segmentArray.push(base);
    } else {
      const altBase = this.getBase({ extension, stem });
      if (altBase) {
        segmentArray.push(altBase);
      }
    }
    const value = segmentArray.join('/');
    return new this.FileSystemPath(value, this.pathBrowserify);
  }

  /**
   * Vytvoří cestu z její hodnoty reprezentované řetězcem.
   * @param {string} value Řetězec s cestou.
   * @returns {FileSystemPath}
   */
  fromValue(value) {
    // TODO Přejmenovat na fromString
    // - Aby odpovídalo změně FileSystemPath#toValue na #toString
    return new this.FileSystemPath(value, this.pathBrowserify);
  }

  /**
   * Zjistí název záznamu z jeho podčástí.
   * @param {Object} [opts={}] Nepovinné parametry.
   * @param {FileSystemName} [opts.extension=''] Přípona názvu záznamu.
   * @param {FileSystemName} [opts.stem=''] Část názvu záznamu bez přípony.
   * @private
   * @returns {FileSystemName} Název záznamu.
   */
  getBase({ extension = '', stem = '' } = {}) {
    const segmentArray = [];
    if (!isString(stem)) {
      throw new TypeError(`Invalid stem "${stem}".`);
    }
    if (stem) {
      segmentArray.push(stem);
    }
    if (!isString(extension)) {
      throw new TypeError(`Invalid extension "${extension}".`);
    }
    if (extension) {
      segmentArray.push(`.${extension}`);
    }
    return segmentArray.join('');
  }

  /**
   * Spojí cesty do jedné výsledné cesty.
   * @param {...FileSystemPath} pathArray Seznam cest ke spojení.
   * @returns {FileSystemPath} Spojená a normalizovaná cesta.
   */
  join(...pathArray) {
    const valueArray = pathArray.map((path) => path.getValue());
    const value = this.pathBrowserify.join(...valueArray);
    return new this.FileSystemPath(value, this.pathBrowserify);
  }

  /**
   * Zjistí relativní cestu mezi dvěmi absolutními.
   * @param {FileSystemPath} fromPath Výchozí cesta.
   * @param {FileSystemPath} toPath Cílová cesta.
   * @returns {FileSystemPath} Relativní cesta.
   */
  relative(fromPath, toPath) {
    const pathArray = [fromPath, toPath];
    const valueArray = pathArray.map((path) => path.getValue());
    const value = this.pathBrowserify.relative(...valueArray);
    return new this.FileSystemPath(value, this.pathBrowserify);
  }

  /**
   * Spojí cesty do jedné absolutní cesty.
   * @param {...FileSystemPath} pathArray Seznam cest ke spojení.
   * @returns {FileSystemPath} Spojená a normalizovaná cesta.
   */
  resolve(...pathArray) {
    const valueArray = pathArray.map((path) => path.getValue());
    const value = this.pathBrowserify.resolve(...valueArray);
    return new this.FileSystemPath(value, this.pathBrowserify);
  }
}

export default FileSystemPathFactory;
